import User from "@mapmycustomers/shared/types/User";

import TargetType from "@app/enum/dashboard/TargetType";
import { isManager, isMember, isReadOnly } from "@app/store/iam/util";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import { GoalConfigurationProperties } from "../types/GoalDetailsProps";

const getGoalConfigurationProperties = (
  currentUser: User,
  scope: Scope,
  users: User[],
  goalConfiguration?: GoalConfiguration,
  teamGoalConfiguration?: GoalConfiguration
): GoalConfigurationProperties => {
  // editing owner or user without a team or the entire org
  if (!scope.teamId) {
    return {
      canToggle: true,
      editable: true,
      individualGoal: false,
      targetBoard: scope,
      value: goalConfiguration?.target,
      visible: true,
    };
  } else if (!scope.userId) {
    // editing team board
    return {
      canToggle: true,
      editable: true,
      individualGoal: false,
      targetBoard: scope,
      value: goalConfiguration?.target,
      visible: true,
    };
  }

  // we're looking at a team member

  // I'm a manager looking at my own board
  if (isManager(currentUser) && scope.userId === currentUser.id) {
    return {
      canToggle: true,
      editable: true,
      individualGoal: false,
      targetBoard: scope,
      value: goalConfiguration?.target,
      visible: true,
    };
  }

  // we're editing a manager
  const scopeUser = users.find(({ id }) => id === scope.userId);
  if (scopeUser && isManager(scopeUser)) {
    // there's no team goal set
    // nothing we can edit then
    if (!teamGoalConfiguration?.enabled) {
      return {
        canToggle: false,
        editable: false,
        individualGoal: false,
        targetBoard: { teamId: scope.teamId },
        visible: false,
      };
    }

    // goal is set for all users including managers
    if (!teamGoalConfiguration.type || teamGoalConfiguration.type === TargetType.TEAM) {
      return {
        canToggle: false,
        editable: false,
        individualGoal: false,
        targetBoard: { teamId: scope.teamId },
        value: teamGoalConfiguration.target,
        visible: true,
      };
    }

    // goal is set only for members, not including managers
    if (teamGoalConfiguration.type === TargetType.MEMBERS) {
      return {
        canToggle: false,
        editable: false,
        individualGoal: false,
        targetBoard: { teamId: scope.teamId },
        visible: false,
      };
    }

    if (teamGoalConfiguration.type === TargetType.INDIVIDUAL) {
      return {
        canToggle: false,
        editable: true,
        individualGoal: true,
        targetBoard: { teamId: scope.teamId },
        value:
          teamGoalConfiguration.individualUserGoals?.[scopeUser.id] ??
          teamGoalConfiguration.defaultTarget,
        visible: true,
      };
    }
  }

  const hasTeamConfiguration = !!teamGoalConfiguration?.enabled;

  // rep is editing a rep
  if (isMember(currentUser)) {
    return {
      canToggle: !hasTeamConfiguration,
      editable: !hasTeamConfiguration,
      individualGoal: teamGoalConfiguration?.type === TargetType.INDIVIDUAL,
      targetBoard: hasTeamConfiguration ? { teamId: scope.teamId } : scope,
      value: hasTeamConfiguration
        ? !teamGoalConfiguration.type
          ? teamGoalConfiguration.target
          : teamGoalConfiguration?.type === TargetType.INDIVIDUAL
          ? teamGoalConfiguration.individualUserGoals?.[scope.userId] ??
            teamGoalConfiguration.defaultTarget
          : teamGoalConfiguration.target
        : goalConfiguration?.target,
      visible: true,
    };
  }

  if (isReadOnly(currentUser)) {
    return {
      canToggle: false,
      editable: false,
      individualGoal: teamGoalConfiguration?.type === TargetType.INDIVIDUAL,
      targetBoard: hasTeamConfiguration ? { teamId: scope.teamId } : scope,
      value: hasTeamConfiguration
        ? !teamGoalConfiguration.type
          ? teamGoalConfiguration.target
          : teamGoalConfiguration?.type === TargetType.INDIVIDUAL
          ? teamGoalConfiguration.individualUserGoals?.[scope.userId] ??
            teamGoalConfiguration.defaultTarget
          : teamGoalConfiguration.target
        : goalConfiguration?.target,
      visible: true,
    };
  }

  // that ||1 at the end is to avoid division by 0. It should never happen, since at least one user - current
  // rep is a member of the team, but added it just in case.
  const numberOfUsersInTeam =
    users.filter((user) => user.teams.some(({ id }) => id === scope.teamId)).length || 1;

  // owner/manager is editing a rep
  return {
    canToggle: !hasTeamConfiguration,
    editable:
      (hasTeamConfiguration && teamGoalConfiguration?.type === TargetType.INDIVIDUAL) ||
      !hasTeamConfiguration,
    individualGoal: teamGoalConfiguration?.type === TargetType.INDIVIDUAL,
    targetBoard: hasTeamConfiguration ? { teamId: scope.teamId } : scope,
    value: hasTeamConfiguration
      ? !teamGoalConfiguration.type || teamGoalConfiguration.type === TargetType.TEAM
        ? (teamGoalConfiguration.target ?? 0) / numberOfUsersInTeam
        : teamGoalConfiguration?.type === TargetType.INDIVIDUAL
        ? teamGoalConfiguration.individualUserGoals?.[scope.userId] ??
          teamGoalConfiguration.defaultTarget
        : teamGoalConfiguration.target
      : goalConfiguration?.target,
    visible: true,
  };
};

export default getGoalConfigurationProperties;
